<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import type {
  ReleaseSigningKey,
  ReleaseSigningKeyCreated,
  ReleaseView,
  UpdateChannel,
} from "@/api/types";
import SecretReveal from "@/components/SecretReveal.vue";

const { t } = useI18n();

const releases = ref<ReleaseView[]>([]);
const keys = ref<ReleaseSigningKey[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);

const productFilter = ref<"" | "agent" | "nx">("");

const newKeyName = ref("");
const newKeyPublicHex = ref("");
const keyFormSubmitting = ref(false);
const keyFormError = ref<string | null>(null);
const lastPrivateKey = ref<{ name: string; private_hex: string } | null>(null);

async function reload() {
  loading.value = true;
  listError.value = null;
  try {
    const [r, k] = await Promise.all([
      api.releases.list(productFilter.value ? { product: productFilter.value } : {}),
      api.releaseKeys.list(),
    ]);
    releases.value = r.releases;
    keys.value = k.keys;
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function promote(r: ReleaseView, channel: UpdateChannel) {
  try {
    await api.releases.promote(r.id, channel);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function revoke(r: ReleaseView) {
  if (!window.confirm(t("admin.releases.confirm_revoke", { version: r.manifest.version }))) return;
  try {
    await api.releases.revoke(r.id);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function submitKey() {
  keyFormError.value = null;
  keyFormSubmitting.value = true;
  // §F15: drop any stale private hex banner from a prior submit so a
  // server-minted secret never lingers next to the wrong key.
  lastPrivateKey.value = null;
  try {
    const body: { name: string; public_key_hex?: string } = { name: newKeyName.value.trim() };
    const pub = newKeyPublicHex.value.trim();
    if (pub) body.public_key_hex = pub;
    const created: ReleaseSigningKeyCreated = await api.releaseKeys.create(body);
    if (created.private_key_hex) {
      lastPrivateKey.value = { name: created.name, private_hex: created.private_key_hex };
    }
    newKeyName.value = "";
    newKeyPublicHex.value = "";
    await reload();
  } catch (err) {
    keyFormError.value = errorMessage(err);
  } finally {
    keyFormSubmitting.value = false;
  }
}

async function disableKey(k: ReleaseSigningKey) {
  if (!window.confirm(t("admin.releases.confirm_disable_key", { name: k.name }))) return;
  try {
    await api.releaseKeys.disable(k.id);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

const filteredKeys = computed(() => keys.value);

onMounted(reload);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.releases") }}</h1>
      <select v-model="productFilter" @change="reload">
        <option value="">{{ t("admin.releases.all_products") }}</option>
        <option value="agent">agent</option>
        <option value="nx">nx</option>
      </select>
    </div>

    <SecretReveal
      v-if="lastPrivateKey"
      :secret="lastPrivateKey.private_hex"
      :label="t('admin.releases.private_shown_once', { name: lastPrivateKey.name })"
      @dismiss="lastPrivateKey = null"
    />

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.releases.key_create_title") }}</h2>
      <form class="admin-form" @submit.prevent="submitKey">
        <label>
          <span>{{ t("admin.releases.key_name") }}</span>
          <input v-model="newKeyName" required :disabled="keyFormSubmitting" />
        </label>
        <label>
          <span>{{ t("admin.releases.key_public_hex") }}</span>
          <input
            v-model="newKeyPublicHex"
            :disabled="keyFormSubmitting"
            :placeholder="t('admin.releases.key_public_hint')"
          />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="keyFormSubmitting">
          {{ keyFormSubmitting ? t("admin.submitting") : t("admin.create") }}
        </button>
      </form>
      <p v-if="keyFormError" class="admin-error" style="margin-top: 0.5rem">{{ keyFormError }}</p>
    </section>

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.releases.keys_title") }}</h2>
      <p v-if="filteredKeys.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.releases.key_name") }}</th>
            <th>{{ t("admin.releases.key_public_hex") }}</th>
            <th>{{ t("admin.status") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="k in filteredKeys" :key="k.id">
            <td :data-label="t('admin.releases.key_name')">{{ k.name }}</td>
            <td class="admin-table__id" :data-label="t('admin.releases.key_public_hex')">
              {{ k.public_key_hex.slice(0, 16) }}…
            </td>
            <td :data-label="t('admin.status')">
              <span class="admin-badge" :data-tone="k.status === 'enabled' ? 'ok' : 'bad'">{{
                t(`admin.status_${k.status}`)
              }}</span>
            </td>
            <td class="admin-table__actions">
              <button v-if="k.status === 'enabled'" type="button" @click="disableKey(k)">
                {{ t("admin.disable") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.releases.list_title") }}</h2>
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="releases.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.releases.product") }}</th>
            <th>{{ t("admin.releases.version") }}</th>
            <th>{{ t("admin.releases.channel") }}</th>
            <th>{{ t("admin.releases.target") }}</th>
            <th>{{ t("admin.status") }}</th>
            <th>{{ t("admin.releases.uploaded_at") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in releases" :key="r.id">
            <td :data-label="t('admin.releases.product')">{{ r.manifest.product }}</td>
            <td :data-label="t('admin.releases.version')">{{ r.manifest.version }}</td>
            <td :data-label="t('admin.releases.channel')">
              {{ r.channel }}
              <template v-if="r.channel !== r.manifest.channel">
                <span class="admin-table__hint"> (signed: {{ r.manifest.channel }})</span>
              </template>
            </td>
            <td :data-label="t('admin.releases.target')">
              {{ r.manifest.os }}/{{ r.manifest.arch }}
            </td>
            <td :data-label="t('admin.status')">
              <span class="admin-badge" :data-tone="r.status === 'published' ? 'ok' : 'bad'">{{
                r.status
              }}</span>
            </td>
            <td :data-label="t('admin.releases.uploaded_at')">
              {{ new Date(r.uploaded_at).toLocaleString() }}
            </td>
            <td class="admin-table__actions">
              <button
                v-if="r.status === 'published' && r.channel === 'canary'"
                type="button"
                @click="promote(r, 'stable')"
              >
                {{ t("admin.releases.promote_stable") }}
              </button>
              <button
                v-if="r.status === 'published' && r.channel === 'stable'"
                type="button"
                @click="promote(r, 'canary')"
              >
                {{ t("admin.releases.promote_canary") }}
              </button>
              <button v-if="r.status === 'published'" type="button" @click="revoke(r)">
                {{ t("admin.releases.revoke") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>
